import React from 'react';
import {OrbitSuspense} from '../../components/base-components/orbit-suspense/orbit-suspense';
import {ServiceBase} from "../ServiceBase";
import {IMiniOrbitService} from "./mini-orbit.service.interface";
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {
    ArenasHeaderButtonConfig,
    MiniOrbitConfiguration
} from '../configurations/config-data-sections/mini-orbit.configuration';
import {Check} from '@sparkware/app-utils';
import {RouteRender} from '../routing-configuration/route-definition/route-definition';
import {UriPath} from '../location/uri-path/uri-path';
import {IControlCenterData, IMiniOrbitCommunicationAdapter} from './adapters/mini-orbit-communication-adapter.interface';
import {VoidCommunicationAdapter} from './adapters/void/void-communication-adapter';
import {MINI_CIP_BASE_ROUTE, MINI_CIS_BASE_ROUTE} from './mini-orbit-consts';

const CmsPageComponent = React.lazy(
    () => import('../../components/cms-page/cms-page.component').then(({CmsPageComponent}) => ({default: CmsPageComponent}))
);
const MiniOrbitPageComponent = React.lazy(
    () => import('../../components/mini-orbit/mini-orbit-page.component').then(({MiniOrbitPageComponent}) => ({default: MiniOrbitPageComponent}))
)

export class MiniOrbitService extends ServiceBase implements IMiniOrbitService {
    private _communicationAdapter: IMiniOrbitCommunicationAdapter;
    private _onKickUserCount = 0;


    constructor(services: IServiceFactoryExtended) {
        super(services);

        this._communicationAdapter = new VoidCommunicationAdapter(services);
        this._currentMiniOrbitConfig = this._extractCurrentMiniOrbitConfig();
        if (this.isEnabled) {
            this.services.splash.onSplashRemoved(() => this._communicationAdapter.onSiteLoaded());
            this._reportAutologinResult();

            this.services.location.enableBasePathOverride(); // this will make the LocationService to prepend /cip to any navigation
            this._communicationAdapter = this.services.miniOrbitCommunicationAdapterFactory.createAdapter(this._currentMiniOrbitConfig.basePath);
            this.reaction(() => this.services.user.logoutReason,
                async (logoutReason) => {
                    // Every time an auto login happens if the user was already logged (having a valid token in the local storage) a logout is first executed.
                    // So by calling _communicationAdapter.onUserKicked immediately after the user is logged out it will trigger another auto login
                    // and we will end up with an infinite reloading of the app.
                    // For this reason we wait for the auto login to complete and if after this the user is still not logged in then we call onUserKicked.
                    if(!this.services.user.isAuthorized) {
                        try {
                            await this.services.user.waitForAutoLogin();
                        } catch (err) {
                            this.services.logger.error('Mini orbit: Waiting for auto login to complete failed!', err);
                        }

                        if(!this.services.user.isAuthorized) {
                            this._onKickUserCount++;
                            this.services.analytics.sendAnalytics('mini_orbit', 'onuserkicked', this._currentMiniOrbitConfig.basePath, this._onKickUserCount);
                            this._communicationAdapter.onUserKicked(logoutReason).then(response => {
                                if(response?.token)
                                {
                                    this.services.user.runAutoLogin(response);
                                }
                            });
                        }
                    }
                });
        }
    }


    private async _reportAutologinResult() {
        try {
            await this.services.user.waitForAutoLogin();
            this._communicationAdapter.onAutologinSuccess();
        } catch (err) {
            this._communicationAdapter.onAutologinFailed();
        }

    };


    private _extractCurrentMiniOrbitConfig() {
        const currentLocationBasePath = this.services.location.currentUri.pathName.getBase().toString().toLowerCase();

        const miniOrbitConfigSection = this.services.configuration.data.miniOrbit;
        if (!Check.isObject(miniOrbitConfigSection)) {
            return null;
        }

        const basePaths = Object.keys(miniOrbitConfigSection);

        for (let i = 0; i < basePaths.length; i++) {
            const basePath = basePaths[i];
            if (basePath?.toLowerCase() === currentLocationBasePath) {
                let miniOrbitConfig = miniOrbitConfigSection[basePath];
                miniOrbitConfig.basePath = basePath;
                return miniOrbitConfig;
            }
        }

        return null;
    }

    private readonly _currentMiniOrbitConfig: MiniOrbitConfiguration = null;

    get currentMiniOrbitConfig(): MiniOrbitConfiguration {
        return this._currentMiniOrbitConfig;
    }

    get isEnabled(): boolean {

        if(this.services.device.os.isPC)
            return  false;

        if (this._currentMiniOrbitConfig) {
            return true;
        }
        return false;
    }

    get isCip(): boolean {
        if(this.isEnabled) {
            return this._currentMiniOrbitConfig.basePath.toLowerCase() === MINI_CIP_BASE_ROUTE;
        }

        return false;
    }

    get isCis(): boolean {
        if(this.isEnabled) {
            return this._currentMiniOrbitConfig.basePath.toLowerCase() === MINI_CIS_BASE_ROUTE;
        }

        return false;
    }

    get fallbackRouteRender(): RouteRender {
        return () => <OrbitSuspense>
            <MiniOrbitPageComponent content={<OrbitSuspense><CmsPageComponent/></OrbitSuspense>} />
        </OrbitSuspense>;
    }

    getCurrentMiniOrbitArenaConfig(): ArenasHeaderButtonConfig {
        let miniOrbitArenas = this.currentMiniOrbitConfig?.arenasHeaderButtons;

        return miniOrbitArenas && miniOrbitArenas.find(arena => this.services.location.currentUri.pathName.equals(new UriPath(arena.link)));
    }

    openUrl = (url: string): void => {
        this._communicationAdapter.openUrlInExternalBrowser(url);
    }

    openControlCenter(data: IControlCenterData): void {
        this._communicationAdapter.openControlCenter(data);
    }
}
